/**
 * PitchRecorder Widget
 *
 * Video recording widget for pitch assessments.
 * Displays a 1/4 questions panel + 3/4 recording interface in a 75vw modal.
 * Supports camera recording, file upload, review with circular buttons, and upload progress.
 *
 * Data persistence:
 * - Recorded/uploaded videos are stored in the Vault (category: 'checkup')
 * - Video reference is submitted via useSubmitPitchVideo SDK hook
 * - Brain uses the vault file for pitch video analysis during MESH calculation
 */

import { cn } from '@tonnie/foundation';
import { useTranslation, useLocale } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import {
  PITCH_AUDIO_BITS_PER_SECOND,
  PITCH_MAX_DURATION_SECONDS,
  PITCH_VIDEO_BITS_PER_SECOND,
  isPitchTooLong,
} from '@tonnie/schemas';
import { useUploadDirect, useSubmitPitchVideo, useRecordStandaloneTest } from '@tonnie/sdk';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tonnie/ui';
import { AlertCircle, CheckCircle2, Loader2, Square, Upload, Video, X } from '@tonnie/assets';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { WidgetProps } from '@tonnie/copilot';

import { classifyMediaError, isExpectedMediaError, type MediaErrorKind } from './media-errors';
import {
  PITCH_FAILURE_KEYS,
  classifyPitchFailure,
  isExpectedPitchFailure,
  type PitchFailure,
} from './pitch-errors';
import { useCheckupReuse } from './reuse/use-checkup-reuse';
import { CheckupReusePrompt } from './reuse/CheckupReusePrompt';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface IPitchRecorderConfig {
  questions: string[];
  disclaimer?: string;
}

/** The limit in whole minutes, for the "too long" copy. */
const PITCH_MAX_MINUTES = PITCH_MAX_DURATION_SECONDS / 60;

// ═══════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════

function useWindowSize() {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const handler = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return size;
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export function PitchRecorderWidget({
  config,
  onComplete,
  onCancel,
}: WidgetProps & { config?: IPitchRecorderConfig }) {
  const { formatPercent } = useLocale();
  const { t } = useTranslation();
  const { captureException, captureMessage } = useErrorCapture();
  const { w: windowWidth, h: windowHeight } = useWindowSize();
  const isSmallScreen = windowWidth < 768;

  // talentId injected by CoPilotGuide from engine variables
  const talentId = (config as Record<string, unknown>)?.talentId as string | undefined;
  // BL-214: a standalone TestIt run additionally banks the pitch artifact to the
  // reusable store. Default/absent = 'checkup' (byte-identical CheckUp path).
  const isTestit =
    ((config as Record<string, unknown>)?.submissionMode as string | undefined) === 'testit';
  // BL-214: inside CheckUp, offer to reuse a valid banked pitch instead of
  // forcing a re-record. Never in a standalone TestIt run (the catalog modal
  // owns that UX there).
  const isCheckup = !isTestit;
  // Read via the 'me' sentinel (JWT-resolved) — the CheckUp-injected talentId is
  // the Clerk id, which is not a valid self-endpoint param.
  const { reusable, isLoading: reuseLoading } = useCheckupReuse('pitch', 'me', {
    enabled: isCheckup,
  });
  const [recordNew, setRecordNew] = useState(false);

  // SDK hooks for vault upload + pitch submission
  const uploadDirectMutation = useUploadDirect();
  const submitPitchMutation = useSubmitPitchVideo(talentId ?? 'me');
  const recordStandalone = useRecordStandaloneTest(talentId ?? 'me');

  const questions = (config as IPitchRecorderConfig)?.questions ?? [];

  const [isRecordingMode, setIsRecordingMode] = useState(false);
  const [isGettingReady, setIsGettingReady] = useState(false);
  const [getReadyCountdown, setGetReadyCountdown] = useState(3);
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingDone, setIsRecordingDone] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(PITCH_MAX_DURATION_SECONDS);
  // Why the last attempt did not reach the CheckUp — shown in place, with its
  // own remedy, instead of one "upload failed" for every cause (F13/F39/N17).
  const [pitchFailure, setPitchFailure] = useState<PitchFailure | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraErrorKind, setCameraErrorKind] = useState<MediaErrorKind | null>(null);

  // The modal belongs to the widget's WORK, not to its mount.
  //
  // Every branch below used to render `<Dialog open>` with the prop hard-coded,
  // so the recorder owned a full-screen `dialog-overlay` for exactly as long as
  // it stayed mounted — and the ONLY thing that unmounted it was the host
  // reacting to `onComplete`. Any host that doesn't (the guide clears
  // `currentFeature` first, so a second `onComplete` hits
  // `if (!currentFeature) return` in use-guide-features.ts and does nothing; a
  // standalone host that only refetches on completion never unmounts at all)
  // left a finished recorder sitting on top of the whole application: the
  // citizen's pitch uploaded fine, and then every click in the app was eaten by
  // an overlay with no relation to anything on screen.
  //
  // Binding `open` to state makes the widget responsible for the surface it
  // opened: when its work ends — completed, reused, or abandoned — it closes,
  // and it cannot block the app afterwards no matter what the host does.
  const [isOpen, setIsOpen] = useState(true);

  /** Hand control back to the host, releasing the modal first. */
  const finish = useCallback(() => {
    setIsOpen(false);
    onComplete?.();
  }, [onComplete]);

  /** Abandon the run, releasing the modal first. */
  const abandon = useCallback(() => {
    setIsOpen(false);
    onCancel?.();
  }, [onCancel]);

  /**
   * Radix only calls `onOpenChange` for ITS OWN dismissals (Escape, overlay
   * click, the close button) — never for our own `setIsOpen(false)` — so
   * routing it to `abandon` cannot double-fire on the completion path.
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) abandon();
    },
    [abandon]
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const videoBlobRef = useRef<Blob | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  /** Wall-clock end of the recording budget — the recording ends HERE. */
  const recordingDeadlineRef = useRef<number>(0);
  // The ACTUAL recorded length (seconds), captured at STOP — not recomputed at
  // submit (which would wrongly include the review time before the user clicks
  // Send). This is the reliable duration; the WebM blob's own metadata reports
  // `Infinity` and can't be trusted.
  const recordedDurationRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Holds the "✓ Video sent" dwell before control returns to the host. */
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 75% viewport sizing
  const dialogWidth = windowWidth * 0.75;
  const dialogHeight = windowHeight * 0.75;

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const startRecordingMode = useCallback(async () => {
    setCameraError(null);
    // Cleared alongside cameraError, or a successful retry leaves a stale
    // `data-camera-error` attribute on a banner that is no longer shown.
    setCameraErrorKind(null);
    setIsRecordingMode(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      // Classify FIRST, then decide how to report. Reporting before classifying
      // is what turned "the citizen declined the camera prompt" into an
      // error-level alert.
      const kind = classifyMediaError(err);
      if (isExpectedMediaError(kind)) {
        // Downgrade, do NOT drop. The RATE of denials is a real signal: a spike
        // after a release means an iframe lost `allow="camera; microphone"`, or
        // a Permissions-Policy header changed. Silently skipping would hide that
        // regression until support tickets arrive. Off the error alert rules,
        // still counted, still trend-able.
        captureMessage(`[copilot.widget] pitch-recorder.getUserMedia.${kind}`, 'warning', {
          component: 'PitchRecorderWidget',
          action: 'startRecordingMode',
          errorName: (err as { name?: string } | null)?.name,
        });
      } else {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'PitchRecorderWidget',
          action: 'startRecordingMode',
        });
      }
      setCameraError(
        kind === 'denied' ? t('copilot.cameraPermissionDenied') : t('copilot.cameraError')
      );
      setCameraErrorKind(kind);
      setIsRecordingMode(false);
    }
  }, [t, captureException, captureMessage]);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
    setIsRecordingDone(true);
    // Capture the recorded length NOW (start → stop), before any review delay.
    // The recording ENDS at the deadline: a tick that a busy or throttled tab
    // delivers late does not make the take longer than the budget it was
    // recorded against, so the length is measured to whichever came first.
    recordedDurationRef.current = recordingStartTimeRef.current
      ? (Math.min(Date.now(), recordingDeadlineRef.current) - recordingStartTimeRef.current) / 1000
      : 0;
    if (timerRef.current) clearInterval(timerRef.current);
    // Stop MediaRecorder — this triggers the 'stop' event which builds the Blob
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    // Stop camera stream
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
  }, []);

  const startGetReady = useCallback(() => {
    setIsGettingReady(true);
    setGetReadyCountdown(3);
    countdownRef.current = setInterval(() => {
      setGetReadyCountdown((prev) => {
        if (prev <= 1) {
          setIsGettingReady(false);
          setIsRecording(true);
          recordingStartTimeRef.current = Date.now();
          recordingDeadlineRef.current =
            recordingStartTimeRef.current + PITCH_MAX_DURATION_SECONDS * 1000;

          // Start MediaRecorder to capture actual video data
          if (streamRef.current) {
            chunksRef.current = [];
            const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
              ? 'video/webm;codecs=vp9,opus'
              : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
                ? 'video/webm;codecs=vp8,opus'
                : 'video/webm';
            // EXPLICIT BITRATES — the root cause of the 76 MB files.
            //
            // With `{ mimeType }` alone the encoder bitrate is entirely
            // browser-chosen, so the size of a fixed-length recording was
            // unpredictable by design: 24 production pitch videos average
            // 31 MB and the four largest are 76 MB — and all four are stuck,
            // because uploads over 50 MB fail four times in five. Pinning the
            // pair makes size a function of length instead of of whatever the
            // browser felt like.
            const recorder = new MediaRecorder(streamRef.current, {
              mimeType,
              videoBitsPerSecond: PITCH_VIDEO_BITS_PER_SECOND,
              audioBitsPerSecond: PITCH_AUDIO_BITS_PER_SECOND,
            });
            recorder.ondataavailable = (e) => {
              if (e.data.size > 0) chunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
              videoBlobRef.current = new Blob(chunksRef.current, { type: mimeType });
            };
            recorder.start(1000); // Collect data every second
            mediaRecorderRef.current = recorder;
          }

          // DEADLINE, not a tick count.
          //
          // The old loop decremented a counter and tested `s <= 0` BEFORE
          // decrementing, so with a 300 s budget the tick at t≈300 s set the
          // display to 0 and the recording kept running; only the tick at
          // t≈301 s stopped it. The length sent is wall-clock (`Date.now()` at
          // start vs stop), so every full-length take posted 301 and the API —
          // which caps at 300 — answered 400. The video was already in the
          // vault by then, so each rejected attempt left a stored file behind.
          //
          // A counter is also wrong for a second reason: a background tab
          // throttles `setInterval` to ~1 Hz-or-slower while `Date.now()` keeps
          // real time, so the counter drifts arbitrarily far behind and the
          // overshoot is unbounded. Recomputing the remaining time from the
          // deadline on every tick is immune to both.
          const deadline = recordingDeadlineRef.current;
          timerRef.current = setInterval(() => {
            const remaining = Math.ceil((deadline - Date.now()) / 1000);
            if (remaining <= 0) {
              setTimerSeconds(0);
              stopRecording();
              return;
            }
            setTimerSeconds(remaining);
          }, 1000);
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stopRecording]);

  const tryAgain = useCallback(() => {
    setIsRecordingDone(false);
    setPitchFailure(null);
    setTimerSeconds(PITCH_MAX_DURATION_SECONDS);
    videoBlobRef.current = null;
    chunksRef.current = [];
    startRecordingMode();
  }, [startRecordingMode]);

  /**
   * Upload a video file/blob to the Vault (category: 'checkup') and submit the
   * pitch video reference via the SDK hook.
   */
  const uploadToVaultAndSubmit = useCallback(
    async (file: File | Blob, fileName: string, durationSec: number) => {
      setIsUploading(true);
      setUploadProgress(0);
      setPitchFailure(null);

      try {
        const mimeType = file instanceof File ? file.type : 'video/webm';
        const fileType = fileName.split('.').pop() || 'webm';
        // Whole seconds, floored — the same rule `isPitchTooLong` applies
        // (D-1), so a take that is a pitch by that rule is never sent as one
        // that is not. Nothing is clamped: a picked file over the limit was
        // refused before this call, and the server re-judges the length from
        // the stored bytes.
        const submittedDuration = Math.floor(durationSec);

        // Step 1+2: presign → PUT straight to storage → confirm.
        //
        // Real progress, reported by the transfer itself, instead of the two
        // hardcoded 0.1 / 0.5 steps that told the citizen nothing about a
        // multi-minute upload. Confirm is where the server sniffs the bytes and
        // reads the recording's real length out of the container header.
        const { fileId } = await uploadDirectMutation.mutateAsync({
          input: {
            fileName,
            fileType,
            category: 'checkup',
            mimeType,
            fileSize: file.size,
          },
          data: file,
          onProgress: (p) => setUploadProgress(p.fraction * 0.9),
        });

        // Step 3: Submit pitch video reference (tags the vault file as the
        // checkup pitch so the MESH calc + reuse find it). Byte-identical for
        // both modes — no assessment record is created here.
        setUploadProgress(0.9);
        await submitPitchMutation.mutateAsync({
          fileId,
          duration: submittedDuration,
        });

        // BL-214: a standalone TestIt run ALSO banks the artifact to the
        // reusable store (source `testit`) so it shows in the catalog and is
        // reconciled into a later CheckUp. The pitch is scored by MESH on reuse,
        // exactly as a CheckUp-taken pitch is — so no score is stored here.
        if (isTestit) {
          await recordStandalone.mutateAsync({
            testId: 'pitch',
            result: { fileId, duration: submittedDuration },
            score: null,
          });
        }

        setUploadProgress(1);
        setIsUploading(false);
        setIsUploaded(true);
        toast.success(t('copilot.videoSubmitted'));
        // Let the citizen see the "✓ Video sent" confirmation, then release the
        // modal AND hand control back in the same beat — `finish` does both, so
        // the overlay never outlives the work it was opened for.
        completeTimerRef.current = setTimeout(finish, 1500);
      } catch (err) {
        const failure = classifyPitchFailure(err);
        if (isExpectedPitchFailure(failure)) {
          captureMessage(`[copilot.widget] pitch-recorder.upload.${failure}`, 'warning', {
            component: 'PitchRecorderWidget',
            action: 'uploadToVaultAndSubmit',
          });
        } else {
          captureException(err instanceof Error ? err : new Error(String(err)), {
            component: 'PitchRecorderWidget',
            action: 'uploadToVaultAndSubmit',
          });
        }
        setIsUploading(false);
        setPitchFailure(failure);
        toast.error(t(PITCH_FAILURE_KEYS[failure], { minutes: PITCH_MAX_MINUTES }));
      }
    },
    [
      uploadDirectMutation,
      submitPitchMutation,
      recordStandalone,
      isTestit,
      finish,
      t,
      captureException,
      captureMessage,
    ]
  );

  const submitRecording = useCallback(() => {
    const blob = videoBlobRef.current;
    if (!blob) {
      toast.error(t('copilot.noVideoData'));
      return;
    }
    // Use the length captured at STOP (see recordedDurationRef) — never
    // recompute at submit, which would include the review time.
    const durationSec = recordedDurationRef.current;
    const fileName = `pitch-video-${Date.now()}.webm`;
    uploadToVaultAndSubmit(blob, fileName, durationSec);
  }, [uploadToVaultAndSubmit, t]);

  /**
   * Read a video File's duration. WebM/MediaRecorder files report
   * `video.duration === Infinity` until the browser is forced to compute it, so
   * on that case we seek to a huge time and read the corrected duration on the
   * `timeupdate` (the well-known Chrome/Firefox workaround). Resolves the
   * UNROUNDED seconds — `isPitchTooLong` floors, and a rounded 300.6 would read
   * as 301 — or `null` when the browser cannot read the file at all (a codec
   * it cannot play, a damaged file). Such a file is refused (F13): it used to
   * be uploaded as a 0-second pitch the company could not play either.
   */
  const getVideoDuration = useCallback((file: File): Promise<number | null> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const finish = (dur: number) => {
        URL.revokeObjectURL(video.src);
        resolve(Number.isFinite(dur) && dur > 0 ? dur : null);
      };
      video.onloadedmetadata = () => {
        if (video.duration === Infinity || Number.isNaN(video.duration)) {
          // Force the browser to resolve the real duration.
          video.currentTime = Number.MAX_SAFE_INTEGER;
          video.ontimeupdate = () => {
            video.ontimeupdate = null;
            finish(video.duration);
            video.currentTime = 0;
          };
        } else {
          finish(video.duration);
        }
      };
      video.onerror = () => finish(Number.NaN);
      video.src = URL.createObjectURL(file);
    });
  }, []);

  // Persistent hidden file input — replaces the prior transient
  // `document.createElement('input')` pattern. Production behaviour is
  // identical (the visible Upload button still opens the OS picker via
  // a programmatic `.click()`), but the persistent element gives E2E
  // tests something stable to target via `setInputFiles` / `file_upload`
  // (the transient input wasn't attached to the DOM when the OS dialog
  // opened, so neither Playwright nor Chrome MCP could intercept it).
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilePicked = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // A refusal belongs to the file it refused. Measuring the NEXT file is
      // asynchronous (seconds for a large recording — it seeks to resolve the
      // real duration), and `uploadToVaultAndSubmit` only clears the banner
      // after that — so for the whole measurement a perfectly valid file sat
      // under the PREVIOUS file's "too long" alert, reading as refused.
      setPitchFailure(null);
      const duration = await getVideoDuration(file);
      // Reset so picking the same file again still fires `change`.
      if (fileInputRef.current) fileInputRef.current.value = '';
      // F13 — a file the browser cannot read has no length anyone can state,
      // and no viewer can play it either. Refused before a byte is uploaded.
      if (duration === null) {
        captureMessage('[copilot.widget] pitch-recorder.upload.unreadable', 'warning', {
          component: 'PitchRecorderWidget',
          action: 'handleFilePicked',
        });
        setPitchFailure('unreadable');
        toast.error(t('copilot.pitch.unreadable'));
        return;
      }
      // F13 — refused BEFORE a byte is uploaded. A too-long file used to be
      // uploaded, stored, and then either rejected (a 400 after the upload) or
      // silently recorded as 300 s; neither told the citizen what to change.
      if (isPitchTooLong(duration)) {
        captureMessage('[copilot.widget] pitch-recorder.upload.too_long', 'warning', {
          component: 'PitchRecorderWidget',
          action: 'handleFilePicked',
        });
        setPitchFailure('too_long');
        toast.error(t(PITCH_FAILURE_KEYS.too_long, { minutes: PITCH_MAX_MINUTES }));
        return;
      }
      uploadToVaultAndSubmit(file, file.name, duration);
    },
    [uploadToVaultAndSubmit, getVideoDuration, captureMessage, t]
  );

  const pickVideoFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ─── Reuse gate (CheckUp only) ───
  // A valid banked pitch already lives in the vault where MESH reads it, so
  // "reuse" just completes the step — no re-upload. "Record a new one" falls
  // through to the normal recorder below.
  if (isCheckup && !recordNew && (reuseLoading || reusable)) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="p-0 flex flex-col items-center justify-center rounded-xl !max-w-none border-border/50 shadow-lg"
          style={{
            width: `${dialogWidth}px`,
            height: `${dialogHeight}px`,
            maxWidth: 'none',
            maxHeight: 'none',
          }}
          closeLabel={t('common.close')}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{t('copilot.pitchRecorder')}</DialogTitle>
            <DialogDescription>{t('copilot.recordYourPitch')}</DialogDescription>
          </DialogHeader>
          {reuseLoading || !reusable ? (
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          ) : (
            <CheckupReusePrompt
              result={reusable}
              onReuse={finish}
              onRecordNew={() => setRecordNew(true)}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Upload / Uploaded view ───
  if (isUploading || isUploaded) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="p-0 flex flex-col items-center justify-center rounded-xl !max-w-none border-border/50 shadow-lg"
          style={{
            width: `${dialogWidth}px`,
            height: `${dialogHeight}px`,
            maxWidth: 'none',
            maxHeight: 'none',
          }}
          closeLabel={t('common.close')}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{t('copilot.pitchRecorder')}</DialogTitle>
            <DialogDescription>
              {isUploading ? t('copilot.uploadingVideo') : t('copilot.videoSent')}
            </DialogDescription>
          </DialogHeader>
          {isUploading ? (
            <>
              <div className="text-5xl mb-14">🎥</div>
              <div className="text-xl font-normal mb-6 text-center text-foreground">
                {t('copilot.uploadingVideo')}
              </div>
              <div className="w-4/5 max-w-md">
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full bg-primary transition-all duration-200"
                    style={{ width: `${uploadProgress * 100}%` }}
                  />
                </div>
                <div className="text-center text-sm text-muted-foreground font-medium">
                  {formatPercent(Math.round(uploadProgress * 100))}
                </div>
              </div>
            </>
          ) : (
            <div data-testid="widget-pitch_recorder-complete">
              <CheckCircle2
                className="mx-auto mb-8 size-24 text-success-accessible"
                aria-hidden="true"
              />
              <div className="text-xl font-normal text-center text-foreground">
                {t('copilot.videoSent')}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Main pitch recorder ───
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-0 overflow-hidden rounded-xl !max-w-none border-border/50 shadow-sm"
        style={{
          width: `${dialogWidth}px`,
          height: `${dialogHeight}px`,
          maxWidth: 'none',
          maxHeight: 'none',
          minHeight: '600px',
        }}
        closeLabel={t('common.close')}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t('copilot.pitchRecorder')}</DialogTitle>
          <DialogDescription>{t('copilot.recordYourPitch')}</DialogDescription>
        </DialogHeader>

        <div className="flex h-full" data-testid="widget-pitch_recorder">
          {/* Persistent hidden file input — replaces the old transient
              document.createElement pattern. Production click path is
              unchanged (Upload button → pickVideoFile → input.click() →
              OS picker), but the persistent DOM element gives E2E tools
              (Playwright setInputFiles, Chrome MCP file_upload) a stable
              target. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            data-testid="widget-pitch_recorder-upload-input"
            onChange={handleFilePicked}
          />
          {/* Left: Questions Panel — 1/4 width */}
          <div className="w-1/4 shrink-0 h-full bg-muted/50 rounded-l-xl p-6 md:p-8 overflow-y-auto border-r border-border/50">
            <div className="text-lg mb-6 font-semibold text-foreground">
              {t('copilot.questions')}
            </div>
            <div className="space-y-6 text-sm">
              {questions.map((q, i) => (
                <div key={i}>
                  <div className="font-semibold text-foreground mb-1.5 text-xs">
                    {t('copilot.questionN', { n: i + 1 })}
                  </div>
                  <div className="text-foreground text-xs leading-relaxed">{q}</div>
                </div>
              ))}
              {config?.disclaimer && (
                <div className="p-3 bg-muted rounded-lg border border-border/50 mt-6">
                  <div className="font-semibold text-foreground mb-1.5 text-xs">
                    {t('copilot.disclaimer')}
                  </div>
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    {config.disclaimer}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Recording interface — 3/4 width */}
          <div className="w-3/4 shrink-0 h-full relative">
            {/* Why the last attempt did not reach the CheckUp (F13/F39/N17).
                Above both the start view and the camera view, so it is seen
                wherever the citizen is when it happens. */}
            {pitchFailure && (
              <div
                role="alert"
                data-testid="widget-pitch_recorder-error"
                data-pitch-error={pitchFailure}
                className="absolute top-4 left-1/2 -translate-x-1/2 z-10 w-[90%] max-w-md p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex gap-3 items-start backdrop-blur-sm"
              >
                <AlertCircle className="w-5 h-5 text-destructive-accessible shrink-0 mt-0.5" />
                <div className="text-sm text-destructive-accessible leading-relaxed">
                  {t(PITCH_FAILURE_KEYS[pitchFailure], { minutes: PITCH_MAX_MINUTES })}
                </div>
              </div>
            )}
            {!isRecordingMode ? (
              /* ─── Start view (bg-card) ─── */
              <div className="h-full bg-card rounded-r-xl flex flex-col justify-center items-center p-12">
                <Button
                  onClick={startRecordingMode}
                  size="lg"
                  className={cn(
                    'rounded-full',
                    isSmallScreen ? 'w-[223px] h-[42px]' : 'w-[368px] h-20'
                  )}
                  data-testid="widget-pitch_recorder-record"
                >
                  <Video className="w-6 h-6 mr-2" />
                  {t('copilot.recordAnswer')}
                </Button>
                <div className={cn('mt-16 px-12 text-center', isSmallScreen && 'mt-6')}>
                  <div className={cn('text-sm text-muted-foreground', isSmallScreen && 'text-2xs')}>
                    {t('copilot.practiceNote')}
                  </div>
                  <div
                    className={cn(
                      'text-sm mt-1 text-muted-foreground',
                      isSmallScreen && 'text-2xs'
                    )}
                  >
                    {t('copilot.uploadNote')}
                  </div>
                </div>

                {/* Camera error message */}
                {cameraError && (
                  // `role="alert"` so a screen-reader user is told the camera
                  // failed — the banner had no announcement at all. The testid
                  // keeps the `widget-pitch_recorder-*` family used by the other
                  // nine ids in this file (see AUTHORING.md note): splitting the
                  // family to satisfy the kebab-case rule would make every future
                  // grep for this widget's anchors miss one.
                  <div
                    role="alert"
                    data-testid="widget-pitch_recorder-camera-error"
                    data-camera-error={cameraErrorKind ?? 'unknown'}
                    className="mt-6 mx-auto max-w-md p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex gap-3 items-start"
                  >
                    <AlertCircle className="w-5 h-5 text-destructive-accessible shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-destructive-accessible mb-1">
                        {t('copilot.cameraErrorTitle')}
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed">
                        {cameraError}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ─── Camera view ─── */
              <div className="h-full bg-black rounded-r-xl relative overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-contain"
                  data-testid="widget-pitch_recorder-preview"
                />

                {/* Get-ready countdown */}
                {isGettingReady && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-6xl text-white font-normal">
                    {getReadyCountdown > 0 ? getReadyCountdown : t('copilot.ready')}
                  </div>
                )}

                {/* Timer — top-16 right-16 */}
                {isRecording && (
                  <div
                    className={cn(
                      'absolute top-16 right-16 text-xl font-bold px-4 py-2 rounded-lg backdrop-blur-sm',
                      timerSeconds <= 10
                        ? 'text-destructive-accessible bg-destructive/20 border border-destructive/50'
                        : 'text-white bg-black/30 border border-white/20'
                    )}
                  >
                    {formatTimer(timerSeconds)}
                  </div>
                )}

                {/* Review overlay — circular buttons */}
                {isRecordingDone && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                    <div className="text-sm text-white mb-6 font-medium">{t('copilot.readyQ')}</div>
                    <div className="flex gap-4 justify-center">
                      <Button
                        onClick={submitRecording}
                        className="w-20 h-20 rounded-full bg-success hover:bg-success/90 text-white text-xs font-medium"
                        data-testid="widget-pitch_recorder-upload"
                      >
                        {t('common.submit')}
                      </Button>
                      <Button
                        onClick={tryAgain}
                        variant="destructive"
                        className="w-20 h-20 rounded-full text-white text-xs font-medium"
                        data-testid="widget-pitch_recorder-retry"
                      >
                        {t('copilot.tryAgain')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Recording controls */}
                {!isRecordingDone && (
                  <div className="absolute bottom-24 left-1/2 -translate-x-1/2">
                    {isRecording ? (
                      <Button
                        onClick={stopRecording}
                        variant="outline"
                        className="w-[70px] h-[70px] rounded-full bg-muted/80 backdrop-blur-sm border-border/50 flex items-center justify-center hover:bg-muted"
                        data-testid="widget-pitch_recorder-stop"
                        aria-label={t('assessmentChat.stopRecording')}
                      >
                        <Square
                          className="w-9 h-9 text-destructive-accessible"
                          aria-hidden="true"
                        />
                      </Button>
                    ) : !isGettingReady ? (
                      <div className="flex gap-8 items-center">
                        <div className="flex flex-col items-center gap-2.5">
                          <Button
                            onClick={pickVideoFile}
                            variant="ghost"
                            className="bg-transparent/50 backdrop-blur-sm hover:bg-white/10 border-none p-2"
                            data-testid="widget-pitch_recorder-upload-trigger"
                            aria-label={t('common.upload')}
                          >
                            <Upload className="w-7 h-7 text-white" aria-hidden="true" />
                          </Button>
                          <span className="text-xs text-white font-medium">
                            {t('common.upload')}
                          </span>
                        </div>
                        <Button
                          onClick={startGetReady}
                          data-testid="widget-pitch_recorder-start"
                          variant="outline"
                          className="w-[70px] h-[70px] rounded-full bg-muted/80 backdrop-blur-sm border-border/50 flex items-center justify-center hover:bg-muted"
                          aria-label={t('assessmentChat.startRecording')}
                        >
                          <div className="w-9 h-9 rounded-full bg-destructive" />
                        </Button>
                        <div className="flex flex-col items-center gap-2.5">
                          <Button
                            onClick={abandon}
                            variant="ghost"
                            className="bg-transparent/50 backdrop-blur-sm hover:bg-white/10 border-none p-2"
                            aria-label={t('common.cancel')}
                          >
                            <X className="w-7 h-7 text-white" aria-hidden="true" />
                          </Button>
                          <span className="text-xs text-white font-medium">
                            {t('common.cancel')}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Bottom instruction bar */}
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-muted/80 backdrop-blur-sm rounded-br-xl flex flex-col justify-center items-center border-t border-border/50">
                  <div className="text-sm text-foreground font-medium">
                    {t('copilot.practiceNote')}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('copilot.uploadNote')}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
